import React from 'react';

/**
 * Librería de íconos propia de Betech — base para reemplazar, de forma
 * gradual, los emojis sueltos que se venían usando en el JSX (⚠️, 🖨️, ➕,
 * 🔒, etc.). Esta fase SOLO construye la librería: todavía no se reemplaza
 * ningún emoji.
 *
 * API: idéntica a lucide-react (que el proyecto ya usa para <ArrowLeft />
 * y para el modal de configuración de impresión). Cada ícono es un
 * componente de función que acepta:
 *   - size         número en px (default 24)
 *   - stroke       color del trazo (default 'currentColor')
 *   - strokeWidth  grosor del trazo (default 1.8)
 *   - className
 *   - ...rest      cualquier prop extra pasa al <svg>. Si se pasa
 *                  aria-label / aria-labelledby / role="img" se omite el
 *                  aria-hidden por defecto (mismo criterio que lucide).
 *
 * Cada <svg> es viewBox="0 0 24 24" fill="none" con
 * strokeLinecap/strokeLinejoin "round".
 *
 * --- Dos formas de uso ---
 *
 * 1) "bare" (botones de tabla, acciones inline, toggle de contraseña del
 *    login): el ícono va SIN círculo.
 *      <IconEditar />                          → hereda el color del texto
 *      <IconEditar stroke={STROKE_DEGRADE} />  → trazo en degradé de marca
 *
 * 2) "insignia" (SOLO Dashboard y menú lateral — lugares destacados):
 *      <InsigniaIcono><IconReportes /></InsigniaIcono>
 *
 * --- El degradé de marca ---
 *
 * NO se define un <defs><linearGradient> por ícono: si se renderizan
 * muchas instancias del mismo ícono a la vez (una tabla con muchas filas)
 * se generarían IDs duplicados en el DOM. En su lugar hay UN ÚNICO
 * gradiente global, montado una sola vez en App.js vía <GradienteMarca />,
 * y todos los íconos en modo "bare" lo referencian con
 * stroke="url(#degradeMarca)" (constante STROKE_DEGRADE).
 */

export const ID_DEGRADE_MARCA = 'degradeMarca';
export const STROKE_DEGRADE = `url(#${ID_DEGRADE_MARCA})`;

// Colores del degradé de marca (mismos que --color-turquesa / --color-purpura).
const MARCA_DESDE = '#1a9b8e';
const MARCA_HASTA = '#6b2d7f';

const ATRIBUTOS_SVG = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function crearIcono(displayName, contenido) {
  function Icono({
    size = 24,
    stroke = 'currentColor',
    strokeWidth = 1.8,
    className = '',
    ...rest
  }) {
    const tieneEtiquetaAccesible =
      'aria-label' in rest || 'aria-labelledby' in rest || rest.role === 'img';
    return (
      <svg
        {...ATRIBUTOS_SVG}
        width={size}
        height={size}
        stroke={stroke}
        strokeWidth={strokeWidth}
        className={className}
        {...(tieneEtiquetaAccesible ? null : { 'aria-hidden': 'true' })}
        {...rest}
      >
        {contenido}
      </svg>
    );
  }
  Icono.displayName = displayName;
  return Icono;
}

/**
 * Definición del degradé de marca. Se monta UNA sola vez, alto en el árbol
 * (App.js), dentro de un <svg> de 0×0 invisible. Todos los íconos "bare"
 * que quieran el trazo en degradé referencian este id vía STROKE_DEGRADE.
 */
export function GradienteMarca() {
  return (
    <svg
      width="0"
      height="0"
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        <linearGradient id={ID_DEGRADE_MARCA} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={MARCA_DESDE} />
          <stop offset="100%" stopColor={MARCA_HASTA} />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * Círculo con el degradé de marca y el ícono hijo centrado, en blanco.
 * SOLO para lugares destacados: tarjetas de estadísticas del Dashboard e
 * ítems del menú lateral. En el resto de la app el ícono va sin círculo
 * (ver STROKE_DEGRADE).
 */
export function InsigniaIcono({ children, size = 52, className = '' }) {
  const tamanoIcono = children?.props?.size ?? Math.round(size * 0.46);
  return (
    <span
      className={['insignia-icono', className].filter(Boolean).join(' ')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${MARCA_DESDE} 0%, ${MARCA_HASTA} 100%)`,
      }}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children, { size: tamanoIcono, stroke: '#ffffff' })
        : children}
    </span>
  );
}

/* ============================================================================
 * LOS 39 ÍCONOS
 * Paths aprobados. El nombre de cada uno indica el concepto que reemplaza.
 * ==========================================================================*/

/* --- Secciones / navegación (menú lateral + accesos del Dashboard) --- */

// 📊  menú "Dashboard" + acción rápida "Ver Reportes"
export const IconDashboard = crearIcono(
  'IconDashboard',
  <>
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.6" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" />
  </>
);

// 👥  menú "Personal" + stat "Personal Activo"
export const IconPersonal = crearIcono(
  'IconPersonal',
  <>
    <circle cx="8.5" cy="7.5" r="3" />
    <path d="M2.5 20c0-3.5 2.7-6 6-6s6 2.5 6 6" />
    <circle cx="16.5" cy="8.5" r="2.5" />
    <path d="M15.2 14.2c2.6.3 4.8 2.4 4.8 5.3" />
  </>
);

// 🧑‍💼  menú "Padrón"
export const IconPadron = crearIcono(
  'IconPadron',
  <>
    <rect x="2.5" y="5" width="19" height="14" rx="2.2" />
    <rect x="5.5" y="8.3" width="4.5" height="4.5" rx="0.8" />
    <line x1="12.5" y1="9" x2="18.5" y2="9" />
    <line x1="12.5" y1="12.3" x2="18.5" y2="12.3" />
    <line x1="5.5" y1="15.7" x2="10" y2="15.7" />
  </>
);

// 🎨  menú "Diseñador" + acción rápida "Diseñar Licencia"
export const IconDisenador = crearIcono(
  'IconDisenador',
  <>
    <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1 0 1.6-.6 1.6-1.4 0-.5-.2-.8-.5-1.2-.3-.4-.5-.7-.5-1.2 0-1 .8-1.7 1.8-1.7h2.1a3.5 3.5 0 0 0 3.5-3.5c0-4.4-3.6-8-8-8z" />
    <circle cx="7.3" cy="10.3" r="1.1" />
    <circle cx="10.2" cy="7.2" r="1.1" />
    <circle cx="14.3" cy="7.2" r="1.1" />
    <circle cx="17" cy="10.3" r="1.1" />
  </>
);

// 📈  menú "Reportes"
export const IconReportes = crearIcono(
  'IconReportes',
  <>
    <line x1="3" y1="20.5" x2="21" y2="20.5" />
    <rect x="5.5" y="13" width="3.4" height="7.5" rx="0.5" />
    <rect x="10.3" y="8" width="3.4" height="12.5" rx="0.5" />
    <rect x="15.1" y="4.5" width="3.4" height="16" rx="0.5" />
  </>
);

// 🔒  menú "Auditoría" (mismo dibujo que IconCandado)
export const IconAuditoria = crearIcono(
  'IconAuditoria',
  <>
    <rect x="5" y="10.5" width="14" height="10" rx="1.8" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    <circle cx="12" cy="15" r="1.4" />
  </>
);

/* --- Circuito de impresión --- */

// 🖨️  "Imprimir" / "Reimprimir" / "Esperando autorización" (Padrón)
export const IconImprimir = crearIcono(
  'IconImprimir',
  <>
    <rect x="6" y="3.5" width="12" height="6" rx="0.8" />
    <path d="M6 15.5H4.2A1.7 1.7 0 0 1 2.5 13.8V9.9A1.7 1.7 0 0 1 4.2 8.2h15.6a1.7 1.7 0 0 1 1.7 1.7v3.9a1.7 1.7 0 0 1-1.7 1.7H18" />
    <rect x="6" y="13" width="12" height="7.5" rx="0.6" />
  </>
);

// 🔄  "Renovar" / "Repetir toma"
export const IconRenovar = crearIcono(
  'IconRenovar',
  <>
    <path d="M20 12a8 8 0 1 1-2.6-5.9" />
    <polyline points="20 3.5 20 7.5 16 7.5" />
  </>
);

// 🔒 / 🔓  "Desactivar" usuario, y base de "Solicitar alta" (mismo dibujo que IconAuditoria)
export const IconCandado = crearIcono(
  'IconCandado',
  <>
    <rect x="5" y="10.5" width="14" height="10" rx="1.8" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    <circle cx="12" cy="15" r="1.4" />
  </>
);

// ⚡  "Revisar y dar de alta" (atajo admin)
export const IconAccionRapida = crearIcono(
  'IconAccionRapida',
  <polygon points="13.2 2.5 5 14 11 14 10.2 21.5 19 10 12.5 10" />
);

// ⏳  stat "Pendientes" (Dashboard + Reportes)
export const IconPendiente = crearIcono(
  'IconPendiente',
  <>
    <circle cx="12" cy="12" r="8.7" />
    <polyline points="12 7.2 12 12 15.5 14" />
  </>
);

// 🎫  stat "Licencias Generadas"
export const IconLicencia = crearIcono(
  'IconLicencia',
  <>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
    <path d="M3.5 8.5h17" />
    <circle cx="16.5" cy="14" r="2.1" />
    <line x1="7" y1="12.3" x2="12.2" y2="12.3" />
    <line x1="7" y1="15.2" x2="10.5" y2="15.2" />
  </>
);

/* --- Estados / feedback --- */

// ✅ / ✓  éxito, "Aprobar", "Usar esta foto", "Guardado", badge "Activo"
export const IconExito = crearIcono(
  'IconExito',
  <polyline points="4.5 12.5 9.5 17.5 19.5 6.5" />
);

// ⚠️  advertencia / error / aviso rojo / miniatura incompleta
export const IconAdvertencia = crearIcono(
  'IconAdvertencia',
  <>
    <path d="M12 3.5 21.5 20H2.5L12 3.5z" />
    <line x1="12" y1="10" x2="12" y2="14.3" />
    <circle cx="12" cy="17" r="0.15" strokeWidth="2.6" />
  </>
);

// ❌  "Rechazar" (Auditoría)
export const IconRechazar = crearIcono(
  'IconRechazar',
  <>
    <circle cx="12" cy="12" r="8.7" />
    <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
    <line x1="15.5" y1="8.5" x2="8.5" y2="15.5" />
  </>
);

// ✕  botón "cerrar" de los modales
export const IconCerrar = crearIcono(
  'IconCerrar',
  <>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </>
);

// 📌  nota / aclaración al pie de un gráfico (Reportes)
export const IconNota = crearIcono(
  'IconNota',
  <>
    <circle cx="12" cy="9" r="3.6" />
    <line x1="12" y1="12.6" x2="12" y2="21" />
  </>
);

/* --- Acciones sobre filas / formularios --- */

// ➕  "Nuevo" / "Agregar texto"
export const IconAgregar = crearIcono(
  'IconAgregar',
  <>
    <line x1="12" y1="4.5" x2="12" y2="19.5" />
    <line x1="4.5" y1="12" x2="19.5" y2="12" />
  </>
);

// ✏️  "Editar"
export const IconEditar = crearIcono(
  'IconEditar',
  <>
    <path d="M4 20l.7-3.9L15.6 5.2a1.9 1.9 0 0 1 2.7 0l1 1a1.9 1.9 0 0 1 0 2.7L8.4 19.8 4 20z" />
    <line x1="13.7" y1="7.1" x2="17.4" y2="10.8" />
  </>
);

// 🗑️  "Eliminar"
export const IconEliminar = crearIcono(
  'IconEliminar',
  <>
    <path d="M5 7h14" />
    <path d="M9.5 7V4.8c0-.7.5-1.3 1.2-1.3h2.6c.7 0 1.2.6 1.2 1.3V7" />
    <path d="M7 7l1 12.5c0 .8.7 1.5 1.5 1.5h5c.8 0 1.5-.7 1.5-1.5L17 7" />
    <line x1="10.2" y1="10.7" x2="10.6" y2="17" />
    <line x1="13.8" y1="10.7" x2="13.4" y2="17" />
  </>
);

// 🔎  buscador del padrón
export const IconBuscar = crearIcono(
  'IconBuscar',
  <>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <line x1="15.3" y1="15.3" x2="20.5" y2="20.5" />
  </>
);

// 👁️  "Vista previa" (Padrón)
export const IconVistaPrevia = crearIcono(
  'IconVistaPrevia',
  <>
    <path d="M2.3 12S5.8 5.5 12 5.5 21.7 12 21.7 12 18.2 18.5 12 18.5 2.3 12 2.3 12z" />
    <circle cx="12" cy="12" r="3.1" />
  </>
);

// ⚙️  "Método de foto" (Padrón) / "Cambiar configuración de impresión" (Diseñador)
export const IconConfiguracion = crearIcono(
  'IconConfiguracion',
  <>
    <circle cx="12" cy="12" r="3.4" />
    <circle cx="12" cy="12" r="7" />
    <line x1="12" y1="2.5" x2="12" y2="5.2" />
    <line x1="12" y1="18.8" x2="12" y2="21.5" />
    <line x1="2.5" y1="12" x2="5.2" y2="12" />
    <line x1="18.8" y1="12" x2="21.5" y2="12" />
    <line x1="5.4" y1="5.4" x2="7.4" y2="7.4" />
    <line x1="16.6" y1="16.6" x2="18.6" y2="18.6" />
    <line x1="18.6" y1="5.4" x2="16.6" y2="7.4" />
    <line x1="7.4" y1="16.6" x2="5.4" y2="18.6" />
  </>
);

/* --- Foto / archivos --- */

// 👤 / 🧑  avatar del usuario, placeholder de foto, "Foto del contribuyente"
export const IconPersona = crearIcono(
  'IconPersona',
  <>
    <circle cx="12" cy="8.3" r="3.8" />
    <path d="M4.5 20.3c0-4.1 3.4-7 7.5-7s7.5 2.9 7.5 7" />
  </>
);

// 📷 / 📸  "Tomar foto con cámara" / "Capturar"
export const IconCamara = crearIcono(
  'IconCamara',
  <>
    <path d="M9 5.5 7.7 8H4.5A1.7 1.7 0 0 0 2.8 9.7v8.6A1.7 1.7 0 0 0 4.5 20h15A1.7 1.7 0 0 0 21.2 18.3V9.7A1.7 1.7 0 0 0 19.5 8h-3.2L15 5.5H9z" />
    <circle cx="12" cy="13.5" r="3.4" />
  </>
);

// 📁  "Subir archivo"
export const IconSubirArchivo = crearIcono(
  'IconSubirArchivo',
  <>
    <line x1="4" y1="20" x2="20" y2="20" />
    <line x1="12" y1="17" x2="12" y2="4" />
    <polyline points="6.5 9.5 12 4 17.5 9.5" />
  </>
);

// 🖼️  "Agregar imagen" (Diseñador)
export const IconImagen = crearIcono(
  'IconImagen',
  <>
    <rect x="2.8" y="4.5" width="18.4" height="15" rx="2" />
    <circle cx="8" cy="9.3" r="1.6" />
    <path d="M2.8 16.5 8.5 11l4 4 3-2.5 5.7 5" />
  </>
);

// 📄  stat "Emitidas Hoy" (Reportes)
export const IconDocumento = crearIcono(
  'IconDocumento',
  <>
    <path d="M6.5 3h8l4 4v13.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
    <polyline points="14.5 3 14.5 7 18.5 7" />
    <line x1="8.5" y1="12" x2="15.5" y2="12" />
    <line x1="8.5" y1="15.5" x2="13" y2="15.5" />
  </>
);

// Copiar al portapapeles (dos hojas superpuestas).
export const IconCopiar = crearIcono(
  'IconCopiar',
  <>
    <rect x="8.5" y="8.5" width="12" height="12" rx="1.5" />
    <path d="M15.5 8.5V4.5a1 1 0 0 0-1-1h-10a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h4" />
  </>
);

// 🏛️  placeholder "Logo" y menú "Logo del municipio" (Diseñador)
export const IconMunicipio = crearIcono(
  'IconMunicipio',
  <>
    <line x1="3" y1="20.5" x2="21" y2="20.5" />
    <polygon points="12 3 20.5 8.5 3.5 8.5" />
    <line x1="5.5" y1="8.5" x2="5.5" y2="20.5" />
    <line x1="9.2" y1="8.5" x2="9.2" y2="20.5" />
    <line x1="14.8" y1="8.5" x2="14.8" y2="20.5" />
    <line x1="18.5" y1="8.5" x2="18.5" y2="20.5" />
  </>
);

/* --- Login / autenticación --- */

// 🔐  ícono dentro del campo "Contraseña" (la llave — distinto de los toggles)
export const IconContrasena = crearIcono(
  'IconContrasena',
  <>
    <circle cx="8.5" cy="8.5" r="4.3" />
    <circle cx="8.5" cy="8.5" r="1.1" />
    <line x1="11.9" y1="11.9" x2="20.5" y2="20.5" />
    <line x1="15.5" y1="16.2" x2="18" y2="13.7" />
    <line x1="18.2" y1="18.8" x2="20.2" y2="16.8" />
  </>
);

// 📧  campo "Email" / "Reenviar invitación"
export const IconEmail = crearIcono(
  'IconEmail',
  <>
    <rect x="2.8" y="5.5" width="18.4" height="13" rx="1.8" />
    <polyline points="3.3 6.5 12 13 20.7 6.5" />
  </>
);

// 🔔  botón de la campana de notificaciones
export const IconNotificaciones = crearIcono(
  'IconNotificaciones',
  <>
    <path d="M6 17V11a6 6 0 0 1 12 0v6l2 2.5H4L6 17z" />
    <path d="M10 20.5a2 2 0 0 0 4 0" />
  </>
);

/* --- Herramientas del canvas del Diseñador --- */

// ➖  "Agregar línea"
export const IconLinea = crearIcono(
  'IconLinea',
  <>
    <circle cx="5.5" cy="18.5" r="1.4" />
    <line x1="7" y1="17" x2="17" y2="7" />
    <circle cx="18.5" cy="5.5" r="1.4" />
  </>
);

// ▭  "Agregar rectángulo"
export const IconRectangulo = crearIcono(
  'IconRectangulo',
  <rect x="3.5" y="6" width="17" height="12" rx="1.4" />
);

// ◯  "Agregar círculo"
export const IconCirculo = crearIcono(
  'IconCirculo',
  <circle cx="12" cy="12" r="8.5" />
);

// ▦  "Agregar QR"
export const IconQR = crearIcono(
  'IconQR',
  <>
    <rect x="3" y="3" width="6.5" height="6.5" rx="0.7" />
    <rect x="14.5" y="3" width="6.5" height="6.5" rx="0.7" />
    <rect x="3" y="14.5" width="6.5" height="6.5" rx="0.7" />
    <rect x="14.5" y="14.5" width="3" height="3" />
    <rect x="18.5" y="18.5" width="2.5" height="2.5" />
    <rect x="14.5" y="18.5" width="2.5" height="2.5" />
    <rect x="18.5" y="14.5" width="2.5" height="2.5" />
  </>
);

/* --- Datos / import-export --- */

// 📤  acción rápida "Importar CSV" (Dashboard)
export const IconImportar = crearIcono(
  'IconImportar',
  <>
    <line x1="4" y1="20" x2="20" y2="20" />
    <line x1="12" y1="4" x2="12" y2="17" />
    <polyline points="6.5 11.5 12 17 17.5 11.5" />
  </>
);

/* --- Toggle mostrar / ocultar contraseña (login + activar cuenta) --- */

// 👁️  estado "mostrar" — MISMO path que IconVistaPrevia, a propósito
export const IconMostrarContrasena = crearIcono(
  'IconMostrarContrasena',
  <>
    <path d="M2.3 12S5.8 5.5 12 5.5 21.7 12 21.7 12 18.2 18.5 12 18.5 2.3 12 2.3 12z" />
    <circle cx="12" cy="12" r="3.1" />
  </>
);

// 🙈  estado "ocultar" — el ojo con una barra diagonal encima
export const IconOcultarContrasena = crearIcono(
  'IconOcultarContrasena',
  <>
    <path d="M2.3 12S5.8 5.5 12 5.5 21.7 12 21.7 12 18.2 18.5 12 18.5 2.3 12 2.3 12z" />
    <circle cx="12" cy="12" r="3.1" />
    <line x1="3.5" y1="20.5" x2="20.5" y2="3.5" />
  </>
);
